import { useState, useEffect, useRef } from 'react';

/**
 * Custom hook for smart dropdown positioning that prevents overflow on narrow screens
 * @param {boolean} isOpen - Whether the dropdown is currently open
 * @param {number} dropdownWidth - Width of the dropdown in pixels (default: 320 for w-80)
 * @param {React.RefObject} externalButtonRef - External button ref to use instead of creating one
 * @returns {object} - { dropdownRef, position, isRightAligned }
 */
const useDropdownPosition = (
  isOpen,
  dropdownWidth = 320,
  externalButtonRef = null
) => {
  const [position, setPosition] = useState({ left: 0, right: 'auto' });
  const [isRightAligned, setIsRightAligned] = useState(false);
  const internalButtonRef = useRef(null);
  const buttonRef = externalButtonRef || internalButtonRef;
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!isOpen || !buttonRef.current) return;

    const updatePosition = () => {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const padding = 16; // 16px padding from screen edge

      // Get the parent container (the relative positioned div)
      const parentContainer = buttonRef.current.parentElement;
      const parentRect = parentContainer.getBoundingClientRect();

      // Detect mobile devices
      const isMobile = window.innerWidth < 640; // sm breakpoint
      const isVeryNarrow = window.innerWidth < 480;

      // Use different width estimates for mobile vs desktop
      const conservativeDropdownWidth = isMobile ? 260 : 400; // Much smaller on mobile

      // Calculate positions in viewport coordinates first
      const buttonLeft = buttonRect.left;
      const buttonRight = buttonRect.right;

      let dropdownLeft = buttonLeft;

      if (isVeryNarrow) {
        // On very narrow screens, center the dropdown
        dropdownLeft = Math.max(
          padding,
          (viewportWidth - conservativeDropdownWidth) / 2
        );
      } else if (isMobile) {
        // On mobile, try to fit within viewport, prefer left alignment
        if (
          dropdownLeft + conservativeDropdownWidth >
          viewportWidth - padding
        ) {
          // If it would overflow right, try to align with button's right edge
          dropdownLeft = buttonRight - conservativeDropdownWidth;
        }
        // Ensure it doesn't go off the left edge
        dropdownLeft = Math.max(padding, dropdownLeft);
      } else {
        // Desktop logic - try right alignment if overflow
        if (
          dropdownLeft + conservativeDropdownWidth >
          viewportWidth - padding
        ) {
          dropdownLeft = buttonRight - conservativeDropdownWidth;
        }
        // Ensure it doesn't go off the left edge
        dropdownLeft = Math.max(padding, dropdownLeft);
      }

      // Ensure dropdown doesn't go off the right edge of viewport
      dropdownLeft = Math.min(
        dropdownLeft,
        viewportWidth - conservativeDropdownWidth - padding
      );

      // Convert back to position relative to parent container
      const leftPosition = dropdownLeft - parentRect.left;

      // Debug logging for Charges dropdown specifically
      if (buttonRef.current?.textContent?.includes('Charges')) {
        console.log('🔍 Charges Dropdown Positioning Debug:', {
          viewportWidth,
          isMobile,
          isVeryNarrow,
          parentRect: {
            left: parentRect.left,
            right: parentRect.right,
            width: parentRect.width
          },
          buttonRect: {
            left: buttonRect.left,
            right: buttonRect.right,
            width: buttonRect.width
          },
          buttonLeft,
          buttonRight,
          conservativeDropdownWidth,
          dropdownLeft,
          leftPosition,
          wouldOverflow:
            buttonLeft + conservativeDropdownWidth > viewportWidth - padding,
          actualDropdownWidth: 'w-96 = 384px'
        });
      }

      setPosition({ left: leftPosition, right: 'auto' });
      setIsRightAligned(false);
    };

    // Update position immediately when dropdown opens
    updatePosition();

    // Update position on window resize
    const handleResize = () => {
      if (isOpen) {
        updatePosition();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen, dropdownWidth, buttonRef]);

  return {
    dropdownRef,
    position,
    isRightAligned
  };
};

export default useDropdownPosition;
